(() => {
  if (window.BBEstimatorReady) return;
  window.BBEstimatorReady = true;
  const instances = new Map();
  let pendingCart = null;
  let cartGeneration = 0;
  const pendingEstimates = new Map();
  const lifecycle = root => {
    const controller = new AbortController();
    const instance = { signal: controller.signal, destroy() { controller.abort(); delete root.dataset.bbEstimateReady; instances.delete(root); } };
    instances.set(root, instance);
    return instance;
  };
  const money = (amount, currency) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2,
  }).format(Number(amount || 0));

  const safeNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : NaN;
  const toMajorDollars = (value) => {
    const cents = safeNumber(value);
    return Number.isFinite(cents) ? Math.max(0, cents / 100) : NaN;
  };

  const estimate = async (endpoint, amount, currency, signal) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('amount', String(Math.max(0, Number(amount) || 0)));
    url.searchParams.set('currency', currency || 'USD');
    const response = await fetch(url, {credentials: 'same-origin', signal, headers: {Accept: 'application/json'}});
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.message || 'Estimate unavailable');
    return payload;
  };

  const estimateVariant = async (endpoint, variantId, quantity, signal) => {
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set('variant_id', String(variantId));
    url.searchParams.set('quantity', String(quantity));
    const response = await fetch(url, {credentials: 'same-origin', signal, headers: {Accept: 'application/json'}});
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.message || 'Canonical variant estimate unavailable');
    return payload;
  };

  const fetchCartEstimate = async (endpoint, cart) => {
    const response = await fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(cart),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.message || 'Canonical cart estimate unavailable');
    return payload;
  };
  // Only coalesce identical in-flight requests. Never cache personalized results.
  const estimateCart = (endpoint, cart) => {
    const key = endpoint + JSON.stringify(cart);
    if (!pendingEstimates.has(key)) {
      const request = fetchCartEstimate(endpoint, cart).finally(() => pendingEstimates.delete(key));
      pendingEstimates.set(key, request);
    }
    return pendingEstimates.get(key);
  };

  const fetchCurrentCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: {Accept: 'application/json'},
      cache: 'no-store',
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok || !Array.isArray(cart.items)) throw new Error('Current cart is unavailable');
    if (!cart.items.length) return null;

    const lines = cart.items.map((item) => {
      if (item.selling_plan_allocation) throw new Error('Selling-plan carts require final order verification');
      const variantId = String(item.variant_id || '');
      const quantity = Number(item.quantity);
      if (!/^\d+$/.test(variantId) || !Number.isSafeInteger(quantity) || quantity < 1) {
        throw new Error('Cart contains an unsupported line');
      }
      return {variantId, quantity};
    });

    const jsonCodes = (cart.cart_level_discount_applications || [])
      .filter((application) => application?.type === 'discount_code')
      .map((application) => application.title);
    const renderedCodes = Array.from(document.querySelectorAll('[data-discount-code]'))
      .map((element) => element.getAttribute('data-discount-code'));
    const discountCodes = [...new Set([...jsonCodes, ...renderedCodes]
      .filter((code) => typeof code === 'string' && code.trim())
      .map((code) => code.trim()))];
    const presentmentCurrency = String(cart.currency || '').toUpperCase();
    if (!/^[A-Z]{3}$/.test(presentmentCurrency)) throw new Error('Cart currency is unavailable');
    return {lines, discountCodes, presentmentCurrency};
  };
  const currentCart = () => {
    if (pendingCart) return pendingCart;
    const request = fetchCurrentCart().finally(() => { if (pendingCart === request) pendingCart = null; });
    return pendingCart = request;
  };

  const getCanonicalCurrency = (root, fallback = 'USD') => (root?.dataset?.shopCurrency || fallback);

  const initProduct = (root) => {
    if (root.dataset.bbEstimateReady) return;
    root.dataset.bbEstimateReady = 'true';
    const instance = lifecycle(root);
    let fetchController, refreshTimer;
    instance.signal.addEventListener('abort', () => { clearTimeout(refreshTimer); fetchController?.abort(); }, { once: true });

    let variants = [];
    try { variants = JSON.parse(root.dataset.variants || '[]'); } catch { instance.destroy(); return; }

    const endpoint = root.dataset.estimateEndpoint;
    const canonicalCurrency = getCanonicalCurrency(root, 'USD');
    const displayCurrency = root.dataset.currency || canonicalCurrency;
    const isCanonicalMarket = displayCurrency === canonicalCurrency;
    const points = root.querySelector('[data-bb-estimate-points]');
    const detail = root.querySelector('[data-bb-estimate-detail]');
    const compact = root.querySelector('[data-bb-product-points]');
    const form = root.closest('product-form, form')
      || root.parentElement?.querySelector('form[action*="/cart/add"]')
      || Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find((candidate) => candidate.querySelector('[name="quantity"]'))
      || document.querySelector('form[action*="/cart/add"]');

    const refresh = async () => {
      const id = form?.querySelector('[name="id"]')?.value
        || form?.querySelector('quantity-selector-component[data-variant-id]')?.dataset.variantId
        || document.querySelector('quantity-selector-component[data-variant-id]')?.dataset.variantId;
      const quantity = Math.max(1, Number(form?.querySelector('[name="quantity"]')?.value || 1));
      const variant = variants.find((entry) => String(entry.id) === String(id)) || variants[0];
      if (!variant || !endpoint) return;
      fetchController?.abort();
      const request = fetchController = new AbortController();
      const valid = () => root.isConnected && !instance.signal.aborted && !request.signal.aborted;

      try {
        let result;
        try {
          // The Rewards backend resolves this variant through Shopify Admin,
          // whose ProductVariant.price is the default shop-currency price.
          result = await estimateVariant(endpoint, variant.id, quantity, request.signal);
        } catch (error) {
          // Compatibility during staged backend rollout: Liquid product money
          // is safe only when the active market already displays shop USD.
          if (!valid() || !isCanonicalMarket) throw error;
          const canonicalPrice = toMajorDollars(variant.price);
          if (!Number.isFinite(canonicalPrice)) throw error;
          result = await estimate(endpoint, canonicalPrice * quantity, canonicalCurrency, request.signal);
        }
        if (!valid()) return;
        const label = `${Number(result.estimatedBp || 0).toLocaleString('en-US')} MP`;

        if (points) points.textContent = label;
        // The rewards service is the single source of truth. Make its USD-normalized
        // basis explicit for international storefront currencies rather than implying
        // that MP are calculated from the displayed currency amount.
        if (compact) compact.textContent = `EST. ${label} · ${money(result.canonicalUsd, 'USD')} ELIGIBLE`;
        if (detail) detail.textContent = `${money(result.canonicalUsd, 'USD')} USD eligible value · ${result.earnRate} MP per $1 USD. Final MP are awarded after qualification.`;
      } catch {
        if (!valid()) return;
        if (points) points.textContent = '— MP';
        if (compact) compact.textContent = 'ESTIMATE UNAVAILABLE IN THIS MARKET';
        if (detail) detail.textContent = isCanonicalMarket
          ? 'Estimated MP is temporarily unavailable.'
          : 'Estimated MP is temporarily unavailable outside the USD market.';
      }
    };

    const scheduleRefresh = () => {
      fetchController?.abort();
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refresh, 50);
    };
    const productComponent = root.closest('product-component');

    form?.addEventListener('change', scheduleRefresh, { signal: instance.signal });
    form?.addEventListener('input', (event) => {
      if (event.target.matches('[name="quantity"]')) scheduleRefresh();
    }, { signal: instance.signal });
    form?.querySelectorAll?.('button[name="plus"], button[name="minus"]')?.forEach((button) => button.addEventListener('click', scheduleRefresh, { signal: instance.signal }));
    productComponent?.addEventListener('change', scheduleRefresh, { signal: instance.signal });
    refresh();
  };

  const initCart = (root) => {
    if (root.dataset.bbEstimateReady) return;
    root.dataset.bbEstimateReady = 'true';
    const instance = lifecycle(root);

    const endpoint = root.dataset.estimateEndpoint;
    const points = root.querySelector('[data-bb-cart-estimate-points]');
    const detail = root.querySelector('[data-bb-cart-estimate-detail]');
    const friendPass = root.parentElement?.querySelector('[data-bb-friend-pass]');
    const referralIdentity = () => {
      try { return localStorage.getItem('bb_rewards_referral') || ''; } catch { return ''; }
    };
    let cartSnapshot;
    let pending = false;
    let revision = 0, rerun = false, retryTimer;
    instance.signal.addEventListener('abort', () => { revision++; clearTimeout(retryTimer); }, { once: true });

    const refresh = async () => {
      if (instance.signal.aborted || !root.isConnected || !endpoint) return;
      const requestRevision = ++revision;
      instance.cartGeneration = cartGeneration;
      if (pending) { rerun = true; return; }
      pending = true;
      const valid = () => root.isConnected && !instance.signal.aborted && requestRevision === revision;

      try {
        cartSnapshot = await currentCart();
        if (!valid()) return;
        if (!cartSnapshot) {
          if (points) points.textContent = '— MP';
          if (detail) detail.textContent = 'A verified cart estimate will appear when the loyalty service is available.';
          if (friendPass) friendPass.hidden = true;
          return;
        }
        const result = await estimateCart(endpoint, cartSnapshot);
        if (!valid()) return;

        if (points) points.textContent = `${Number(result.estimatedBp || 0).toLocaleString('en-US')} MP`;
        if (detail) detail.textContent = `${money(result.canonicalUsd, 'USD')} USD eligible merchandise · ${result.earnRate} MP per $1 USD. Shipping, tax, duties and gift cards are excluded.`;

        if (friendPass) {
          const eligible = referralIdentity() && Number(result.canonicalUsd || 0) >= 100;
          friendPass.hidden = !eligible;
          const friendDetail = friendPass.querySelector('[data-bb-friend-pass-detail]');
          if (friendDetail) friendDetail.textContent = eligible
            ? 'Use the referral that brought you here. Your highest eligible first-order offer will be verified securely.'
            : 'A Friend Pass needs a verified referral and $100 USD eligible merchandise.';
        }
      } catch {
        if (!valid()) return;
        cartSnapshot = null;
        if (points) points.textContent = '— MP';
        if (detail) detail.textContent = 'A verified cart estimate will appear when the loyalty service is available.';
        if (friendPass) friendPass.hidden = true;
        // Shopify can replace cart markup before its AJAX cart is ready. Retry once
        // against the same server-authoritative endpoint; never calculate locally.
        if (root.dataset.bbEstimateRetried !== 'true') {
          root.dataset.bbEstimateRetried = 'true';
          retryTimer = window.setTimeout(refresh, 750);
        }
      } finally { pending = false; if (rerun) { rerun = false; refresh(); } }
    };
    instance.refresh = refresh;
    instance.invalidate = () => { revision++; clearTimeout(retryTimer); };

    friendPass?.querySelector('[data-bb-friend-pass-claim]')?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      if (!cartSnapshot?.lines?.length || !referralIdentity()) return;
      button.disabled = true;
      try {
        const response = await fetch(friendPass.dataset.endpoint, {
          method: 'POST', credentials: 'same-origin',
          headers: {'Content-Type': 'application/json', Accept: 'application/json'},
          body: JSON.stringify({...cartSnapshot, referralIdentity: referralIdentity()}),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.message || 'Friend Pass could not be activated.');
        const code = payload.discountCode;
        if (!code) throw new Error('Friend Pass code was not issued.');
        friendPass.replaceChildren(Object.assign(document.createElement('a'), {
          href: `/discount/${encodeURIComponent(code)}?redirect=/cart`, textContent: 'APPLY FRIEND PASS',
        }));
      } catch (error) {
        const friendDetail = friendPass.querySelector('[data-bb-friend-pass-detail]');
        if (friendDetail) friendDetail.textContent = error.message || 'Friend Pass could not be activated.';
        button.disabled = false;
      }
    }, { signal: instance.signal });

    // Liquid supplies the confirmed initial quantity; an empty Bag needs no fetch.
    if (root.dataset.cartItemCount !== '0') refresh();
  };

  const initialize = (scope = document) => {
    if (scope.matches?.('[data-bb-product-estimator]')) initProduct(scope);
    if (scope.matches?.('[data-bb-cart-estimator]')) initCart(scope);
    scope.querySelectorAll?.('[data-bb-product-estimator]').forEach(initProduct);
    scope.querySelectorAll?.('[data-bb-cart-estimator]').forEach(initCart);
  };
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) if (event.target.contains(root)) instance.destroy();
  });
  let rescanTimer;
  const refreshCarts = () => {
    clearTimeout(rescanTimer);
    rescanTimer = setTimeout(() => {
      initialize();
      for (const instance of instances.values()) if (instance.cartGeneration !== cartGeneration) instance.refresh?.();
    }, 50);
  };

  const start = () => {
    initialize();
    if (document.documentElement.dataset.bbEstimatorObserving) return;
    document.documentElement.dataset.bbEstimatorObserving = 'true';
    // Theme cart implementations often replace their summary asynchronously.
    // Re-scan after their standard cart events and native cart controls settle.
    ['cart:refresh', 'cart:updated', 'shopify:cart:refresh', 'shopify:cart:updated'].forEach((name) => document.addEventListener(name, () => { cartGeneration++; pendingCart = null; refreshCarts(); }));
    document.addEventListener('shopify:cart:lines-update', (event) => {
      cartGeneration++; pendingCart = null;
      for (const instance of instances.values()) instance.invalidate?.();
      event.promise?.then(({detail}) => { if (!detail?.didError) refreshCarts(); }).catch(() => {});
    });
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          initialize(node);
        }
      }
      for (const [root, instance] of instances) if (!root.isConnected) instance.destroy();
    });
    observer.observe(document.body, {childList: true, subtree: true});
    window.addEventListener('pagehide', () => { clearTimeout(rescanTimer); pendingCart = null; for (const instance of instances.values()) instance.destroy(); });
    window.addEventListener('pageshow', event => { if (event.persisted) { cartGeneration++; initialize(); refreshCarts(); } });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();
